import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { Box, Button, Card, CardContent, Container, IconButton, MenuItem, Typography, CircularProgress, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Drawer, useMediaQuery, Theme, createFilterOptions } from "@mui/material";
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import Navbar from "../../layouts/Navbar/Navbar";
import { Sidebar } from "../HomePage/partials/Sidebar/Sidebar";
import { EXAM_RESULTS_LOGO_URL, EXAM_SELECT_CODE, PREBOARD_STORAGE_KEY } from "../../utils/constants";
import { PinnacleTextField } from "../../components/PinnacleTextField";
import { getActivatedCodes } from "../../api/services/code";
import { handleApiError } from "../../utils/errorHandler";
import { pushNotification } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { NotificationTypes } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { ExamCodeResponse } from "../../api/interfaces/Code";
import { ValidateTokenData } from "../../api/interfaces/Auth";
import { validate } from "../../api/services/auth";
import { ExamResultResponse, ExamResultsResponse } from "../../api/interfaces/Exam";
import { getExamResultsByCodeId } from "../../api/services/exam";
import PinnacleAutocomplete from "../../components/PinnacleAutocomplete/PinnacleAutocomplete";
import PinnacleDrawer from "../../components/PinnacleDrawer/PinnacleDrawer";
import ExamHistoryDetails from "./drawer/ExamHistoryDetails";
import { formatPercentage } from "../../utils/formatPercentage";

const ExamResults: React.FC = () => {
    const navigate = useNavigate();
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState<boolean>(false);

    const [currentUser, setCurrentUser] = useState<ValidateTokenData | null>(null);
    const [examCodes, setExamCodes] = React.useState<ExamCodeResponse[]>([]);
    const [examResults, setExamResults] = React.useState<ExamResultsResponse | null>(null);
    const [selectedExam, setSelectedExam] = React.useState<ExamResultResponse | null>(null);

    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

    const [selectedExamCode, setSelectedExamCode] = React.useState<string>('');
    const [selectedExamCodeName, setSelectedExamCodeName] = React.useState<string>('');
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [shouldRefetchExamCodes, setShouldRefetchExamCodes] = useState(false);

    useEffect(() => {
        const run = async () => {
            if (shouldRefetchExamCodes) {
                try {
                    const getCodes = await getActivatedCodes();
                    setExamCodes(getCodes.data);
                } catch (error) {
                    handleApiError(error, {
                        notify: (msg) => {
                            pushNotification({
                                details: msg,
                                type: NotificationTypes.Error,
                            });
                        },
                    });
                } finally {
                    setShouldRefetchExamCodes(false);
                }
            }
        };
        run();
    }, [shouldRefetchExamCodes]);

    useEffect(() => {
        const run = async () => {
            const token =
                localStorage.getItem(PREBOARD_STORAGE_KEY) ||
                sessionStorage.getItem(PREBOARD_STORAGE_KEY);
            if (!token) {
                navigate('/login');
                return;
            }
            try {
                setIsLoading(true);
                const validateResponse = await validate();
                setCurrentUser(validateResponse.data as unknown as ValidateTokenData);

                const getCodes = await getActivatedCodes();

                setExamCodes(getCodes.data);
            } catch (error: any) {
                // trigger handleApiError is status code is not 401
                if (error.response?.status !== 401) {
                    handleApiError(error, {
                        notify: (msg) => {
                            pushNotification({
                                details: msg,
                                type: NotificationTypes.Error,
                            });
                        },
                    });
                }
            } finally {
                setIsLoading(false);
            }
        };
        run();

    }, [navigate]);

    const refetchExams = useCallback(() => {
        setShouldRefetchExamCodes(prev => !prev);
    }, []);

    const onChange = async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const codeId = e.target.value;
        setSelectedExamCode(codeId);
        try {
            setIsLoading(true);
            const getExamResults = await getExamResultsByCodeId(codeId);

            setExamResults(getExamResults.data);
        } catch (error: any) {
            handleApiError(error, {
                notify: (msg) => {
                    pushNotification({
                        details: msg,
                        type: NotificationTypes.Error,
                    });
                },
            });
        } finally {
            setIsLoading(false);
        }
    }

    const handleViewExamAttemptDetails = (exam: ExamResultResponse) => {
        setSelectedExam(exam)
        setIsDrawerOpen(true);
    }

    const selectedExamOption = examCodes.find((c) => c._id === selectedExamCode) || null;

    return (
        <>
            <Navbar name={`${currentUser?.firstName} ${currentUser?.lastName}`} onOpenSidebar={() => setMobileSidebarOpen(true)} />
            <main
                style={{
                    display: "flex",
                    width: "100%",
                    minHeight: "100vh",
                    backgroundColor: "#f6f6f6",
                    overflow: "hidden",
                }}
            >
                <Box
                    component="section"
                    sx={{
                        flexGrow: 1,
                        width: "100%",
                        overflowX: "hidden",
                        backgroundColor: "#EEF0FA",
                    }}
                >
                    <Container
                        maxWidth={false}
                        sx={{
                            mt: { xs: 4, sm: 6 },
                            mb: 4,
                            px: { xs: 2, sm: 3, lg: 15 },
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", md: "row" },
                                gap: 3,
                            }}
                        >
                            <Box
                                sx={{
                                    flexShrink: 0,
                                    width: { xs: "100%", md: "25%" },
                                    backgroundColor: '#fff',
                                    borderRadius: '8px',
                                    overflow: "hidden",
                                }}
                            >
                                <Box
                                    sx={{
                                        flexShrink: 0,
                                        width: { xs: "100%", md: "auto" },
                                        display: { xs: "none", md: "block" },
                                    }}
                                >
                                    <Sidebar variant="desktop" user={currentUser || {}} onSuccessActivateCode={refetchExams} />
                                </Box>

                                <Drawer
                                    anchor="left"
                                    open={mobileSidebarOpen}
                                    onClose={() => setMobileSidebarOpen(false)}
                                    slotProps={{
                                        paper: {
                                            sx: {
                                                width: 280,
                                                backgroundColor: "#F6F6F6",
                                                overflow: "hidden",

                                            },
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height: "100%",
                                            overflowY: "auto",
                                            p: 2,
                                        }}
                                    >

                                        <Sidebar
                                            variant="mobile"
                                            user={currentUser || {}}
                                            onSuccessActivateCode={refetchExams}
                                        />
                                    </Box>
                                </Drawer>
                            </Box>

                            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                                <Box sx={{ width: "100%", overflow: "hidden" }}>
                                    <Card sx={{ borderRadius: '8px', '--Paper-shadow': 'none !important' }}>
                                        <CardContent sx={{ p: 3 }}>
                                            {/* Header */}
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "space-between",
                                                    alignItems: "center",
                                                    mb: 3,
                                                }}
                                            >
                                                {/* Group these two items so they stay together */}
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <IconButton
                                                        sx={{ p: 0, mr: 1 }}
                                                        onClick={() => navigate(-1)}
                                                    >
                                                        <ArrowBackOutlinedIcon sx={{
                                                            fontSize: 26,
                                                            color: "#373737"
                                                        }}
                                                        />
                                                    </IconButton>
                                                    <Typography variant="h3" fontWeight={600}>
                                                        Results
                                                    </Typography>
                                                </Box>

                                                <Box
                                                    component="img"
                                                    src={EXAM_RESULTS_LOGO_URL}
                                                    alt="Select code image"
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        objectFit: "contain",
                                                    }}
                                                />
                                            </Box>

                                            <Box>
                                                <Typography variant="body2" sx={{ color: "#5840BA", pb: 2 }}>
                                                    Exam Code:
                                                </Typography>
                                                <PinnacleAutocomplete
                                                    id="examCodeId"
                                                    label="Select an Exam Code"
                                                    options={examCodes ?? []}
                                                    value={selectedExamOption}
                                                    isOptionEqualToValue={(option, value) => option._id === value._id}
                                                    disabled={isLoading}
                                                    placeholder="Search exam code…"

                                                    onChange={(_, newValue) => {
                                                        if (!newValue) return;

                                                        setSelectedExamCode(newValue._id);
                                                        setSelectedExamCodeName(newValue.code)

                                                        // trigger existing fetch logic
                                                        onChange({
                                                            target: { value: newValue._id },
                                                        } as React.ChangeEvent<HTMLInputElement>);
                                                    }}

                                                    getOptionLabel={(option: any) => option.code ?? ""}

                                                    renderInput={(params) => (
                                                        <PinnacleTextField
                                                            {...params}
                                                            label="Select an Exam Code"
                                                            variant="outlined"
                                                            name="examCode"
                                                            autoComplete="off"
                                                        />
                                                    )}

                                                    renderOption={(props, option) => {
                                                        const { key, ...optionProps } = props;
                                                        return (
                                                            <li key={key} {...optionProps}>
                                                                <Typography
                                                                    noWrap
                                                                    sx={{
                                                                        maxWidth: 240,
                                                                        overflow: "hidden",
                                                                        textOverflow: "ellipsis",
                                                                        fontSize: "14px",
                                                                    }}
                                                                    title={option.code}
                                                                >
                                                                    {option.code}
                                                                </Typography>
                                                            </li>
                                                        );
                                                    }}
                                                />
                                            </Box>

                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    py: 5,
                                                    width: "100%",
                                                }}
                                            >
                                                {isLoading ? (
                                                    // Loader
                                                    <CircularProgress color="primary" />
                                                ) : selectedExamCode && (examResults && !examResults.exams.length) ? (
                                                    // No results for selected code
                                                    <>
                                                        {/* <Box
                                                            component="img"
                                                            src={EMPTY_STATE_ILLUSTRATION_URL}
                                                            alt="No Exams Illustration"
                                                            sx={{
                                                                width: 200,
                                                                height: 200,
                                                                mb: 2,
                                                                opacity: 0.85,
                                                            }}
                                                        /> */}
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: "#818181",
                                                                fontWeight: 500,
                                                                fontSize: "12px",
                                                                mt: 0.5,
                                                            }}
                                                        >
                                                            No data to display
                                                        </Typography>
                                                    </>
                                                ) : !selectedExamCode ? (
                                                    // Prompt to select an exam code
                                                    <>
                                                        <Typography
                                                            variant="body1"
                                                            sx={{
                                                                color: "#818181",
                                                                mt: 0.5,
                                                                textAlign: "center",
                                                                whiteSpace: "pre-line",
                                                            }}
                                                        >
                                                            {`Start by choosing an exam code \n in the dropdown.`}
                                                        </Typography>
                                                    </>
                                                ) : (
                                                    <>
                                                        {selectedExamCode && (examResults && examResults.exams) && (
                                                            <>
                                                                <TableContainer
                                                                    component={Paper}
                                                                    sx={{
                                                                        mt: 2,
                                                                        boxShadow: 'none',
                                                                        backgroundColor: 'transparent',
                                                                        overflowX: "auto",
                                                                    }}
                                                                >
                                                                    <Table sx={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                                                                        <TableHead>
                                                                            <TableRow sx={{ '& th': { border: 'none' } }}>
                                                                                <TableCell
                                                                                    sx={{
                                                                                        color: "#5840BA",
                                                                                        fontWeight: 500,
                                                                                        fontSize: { xs: "11px", sm: "13px" },
                                                                                        whiteSpace: { xs: "normal", sm: "nowrap" },
                                                                                    }}
                                                                                >
                                                                                    Exam Name
                                                                                </TableCell>

                                                                                <TableCell
                                                                                    sx={{
                                                                                        color: "#5840BA",
                                                                                        fontWeight: 500,
                                                                                        fontSize: { xs: "11px", sm: "13px" },
                                                                                        whiteSpace: { xs: "normal", sm: "nowrap" },
                                                                                    }}
                                                                                >
                                                                                    {isMobile ? "# of correct items" : "# of correct items / total # of items"}
                                                                                </TableCell>

                                                                                <TableCell
                                                                                    sx={{
                                                                                        color: "#5840BA",
                                                                                        fontWeight: 500,
                                                                                        fontSize: { xs: "11px", sm: "13px" },
                                                                                        whiteSpace: { xs: "normal", sm: "nowrap" },
                                                                                    }}
                                                                                >
                                                                                    Raw Grade
                                                                                </TableCell>

                                                                                <TableCell
                                                                                    sx={{
                                                                                        color: "#5840BA",
                                                                                        fontWeight: 500,
                                                                                        fontSize: { xs: "11px", sm: "13px" },
                                                                                        whiteSpace: { xs: "normal", sm: "nowrap" },
                                                                                    }}
                                                                                >
                                                                                    Adjustment
                                                                                </TableCell>

                                                                                <TableCell
                                                                                    sx={{
                                                                                        color: "#5840BA",
                                                                                        fontWeight: 500,
                                                                                        fontSize: { xs: "11px", sm: "13px" },
                                                                                        whiteSpace: { xs: "normal", sm: "nowrap" },
                                                                                    }}
                                                                                >
                                                                                    Final Grade
                                                                                </TableCell>

                                                                                <TableCell />
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {examResults.exams.map((exam: ExamResultResponse, i: number) => (
                                                                                <TableRow
                                                                                    key={exam._id}
                                                                                    sx={{
                                                                                        backgroundColor: i % 2 === 0 ? "#F7F8FD" : "#FFFFFF",
                                                                                        '& td': { border: 'none' }
                                                                                    }}
                                                                                >
                                                                                    <TableCell sx={{ maxWidth: { xs: 120, sm: 200 } }}>
                                                                                        <Typography
                                                                                            noWrap
                                                                                            sx={{
                                                                                                fontSize: "12px",
                                                                                                fontWeight: 400,
                                                                                                maxWidth: "100%",
                                                                                                overflow: "hidden",
                                                                                                textOverflow: "ellipsis",
                                                                                                whiteSpace: "nowrap",
                                                                                            }}
                                                                                        >
                                                                                            {exam.name}
                                                                                        </Typography>
                                                                                    </TableCell>
                                                                                    <TableCell sx={{ maxWidth: { xs: 80, sm: 140 } }}>
                                                                                        <Typography
                                                                                            noWrap
                                                                                            sx={{
                                                                                                fontSize: "12px",
                                                                                                overflow: "hidden",
                                                                                                textOverflow: "ellipsis",
                                                                                                textAlign: "center",
                                                                                            }}
                                                                                        >
                                                                                            {exam.highestScore === null ? "TBA" : exam.highestScore} / {exam.numberOfQuestions}
                                                                                        </Typography>
                                                                                    </TableCell>
                                                                                    <TableCell sx={{ maxWidth: { xs: 70, sm: 100 } }}>
                                                                                        <Typography
                                                                                            noWrap
                                                                                            sx={{
                                                                                                fontSize: "12px",
                                                                                            }}
                                                                                        >
                                                                                            {formatPercentage(exam.rawGrade)}
                                                                                        </Typography>
                                                                                    </TableCell>

                                                                                    <TableCell sx={{ maxWidth: { xs: 70, sm: 100 } }}>
                                                                                        <Typography
                                                                                            noWrap
                                                                                            sx={{
                                                                                                fontSize: "12px",
                                                                                            }}
                                                                                        >
                                                                                            {formatPercentage(exam.adjustment)}
                                                                                        </Typography>
                                                                                    </TableCell>

                                                                                    <TableCell sx={{ maxWidth: { xs: 70, sm: 100 } }}>
                                                                                        <Typography
                                                                                            noWrap
                                                                                            sx={{
                                                                                                fontSize: "12px",
                                                                                            }}
                                                                                        >
                                                                                            {formatPercentage(exam.grade)}
                                                                                        </Typography>
                                                                                    </TableCell>
                                                                                    <TableCell>
                                                                                        <Button
                                                                                            variant="outlined"
                                                                                            size="small"
                                                                                            endIcon={<VisibilityOutlinedIcon sx={{ fontSize: "12px" }} />}
                                                                                            sx={{
                                                                                                borderRadius: "4px",
                                                                                                borderColor: "#5840BA",
                                                                                                color: "#5840BA",
                                                                                                textTransform: "none",
                                                                                                fontSize: "12px",
                                                                                                fontWeight: 400,
                                                                                                backgroundColor: "#FFFFFF",
                                                                                                minWidth: "90px",
                                                                                                "&:hover": { backgroundColor: "#F7F5FF", borderColor: "#5840BA" },
                                                                                            }}
                                                                                            onClick={() => handleViewExamAttemptDetails(exam)}
                                                                                        >
                                                                                            Details
                                                                                        </Button>
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            ))}

                                                                            <TableRow sx={{ backgroundColor: "transparent", '& td': { border: 'none' } }}>
                                                                                <TableCell />
                                                                                <TableCell
                                                                                    sx={{
                                                                                        textAlign: 'right',
                                                                                        color: "#5840BA",
                                                                                        fontSize: "14px",
                                                                                        fontWeight: 500
                                                                                    }}
                                                                                >
                                                                                    Raw Average:
                                                                                </TableCell>

                                                                                <TableCell sx={{ color: "#373737" }}>
                                                                                    {examResults.rawAverage === null ? 'TBA' : `${examResults.rawAverage}%`}
                                                                                    
                                                                                </TableCell>

                                                                                <TableCell
                                                                                    sx={{
                                                                                        textAlign: 'right',
                                                                                        color: "#5840BA",
                                                                                        fontSize: "14px",
                                                                                        fontWeight: 500,
                                                                                        p: 0 // remove extra padding
                                                                                    }}
                                                                                >
                                                                                    Final Average:
                                                                                </TableCell>

                                                                                <TableCell sx={{ color: "#373737" }}>
                                                                                    {examResults.average === null ? 'TBA' : `${examResults.average}%`}
                                                                                </TableCell>

                                                                                <TableCell />
                                                                            </TableRow>
                                                                        </TableBody>
                                                                    </Table>
                                                                </TableContainer>
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Box>
                            </Box>
                        </Box>
                    </Container>
                </Box>
            </main>

            <PinnacleDrawer
                title={selectedExam?.name ?? ''}
                isDrawerOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
                customWidthSize={600}
            >
                <ExamHistoryDetails
                    examCodeId={selectedExamCode}
                    examCodeName={selectedExamCodeName}
                    exam={selectedExam ?? null}
                />
            </PinnacleDrawer>
        </>
    );
};

export default ExamResults;
